import { useEffect, useState } from "react";
import { Gift, Sparkles } from "lucide-react";
import { PRIZE_WHEEL, PRIZE_TYPES, WHEEL_TERMS } from "@/constants/testIds";

/**
 * SpinWheelPreview — a non-interactive teaser rendered on Step 1 of the
 * event registration form.
 *
 * • Draws the same 53-slot layout as the interactive wheel so the visual
 *   language is consistent across the pre-registration and post-registration
 *   experience.
 * • Rotates slowly on its own — pure CSS keyframes, no state churn.
 * • Emphasises the "every customer gets an assured gift" promise plus the
 *   T&C strip so buyers know upfront that the reward is conditional on the
 *   Sept 12–13, 2026 launch visit + plot booking.
 */
const SIZE = 260;
const RADIUS = SIZE / 2;
const CENTER = RADIUS;

export default function SpinWheelPreview() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // Delay one tick so the CSS transition kicks in on first paint.
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const prizes = PRIZE_WHEEL;
  const sliceAngle = 360 / prizes.length;

  return (
    <div className="rounded-2xl border border-lime/30 bg-gradient-to-br from-cream/70 via-white to-lime/10 p-5 md:p-6 mb-8">
      <div className="grid md:grid-cols-[auto,1fr] gap-6 md:gap-8 items-center">
        {/* Slowly-rotating preview wheel */}
        <div className="relative mx-auto md:mx-0" style={{ width: SIZE, height: SIZE }}>
          <div className="absolute inset-[-10px] rounded-full bg-gradient-to-br from-lime/30 via-teal/10 to-transparent blur-lg pointer-events-none" />
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-20">
            <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[18px] border-t-lime drop-shadow" />
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-4 border-forest z-10 grid place-items-center">
            <Sparkles size={18} className="text-teal" />
          </div>
          <div
            className="absolute inset-0 rounded-full overflow-hidden border-4 border-forest shadow-[0_16px_40px_-16px_rgba(11,74,61,0.5)]"
            style={{
              transform: `rotate(${mounted ? 720 : 0}deg)`,
              transition: "transform 24s linear",
            }}
          >
            <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} className="block">
              {prizes.map((p, i) => {
                const start = (i * sliceAngle - 90) * (Math.PI / 180);
                const end = ((i + 1) * sliceAngle - 90) * (Math.PI / 180);
                const x1 = CENTER + RADIUS * Math.cos(start);
                const y1 = CENTER + RADIUS * Math.sin(start);
                const x2 = CENTER + RADIUS * Math.cos(end);
                const y2 = CENTER + RADIUS * Math.sin(end);
                const d = `M ${CENTER} ${CENTER} L ${x1} ${y1} A ${RADIUS} ${RADIUS} 0 0 1 ${x2} ${y2} Z`;
                return (
                  <path
                    key={`${p.id}-${i}`}
                    d={d}
                    fill={p.color}
                    stroke="#F4F4EF"
                    strokeWidth={0.5}
                  />
                );
              })}
            </svg>
          </div>
        </div>

        {/* Promise + prize legend */}
        <div>
          <div className="eyebrow text-teal inline-flex items-center gap-2">
            <Gift size={13} /> Assured Gift on Registration
          </div>
          <h3 className="font-display text-forest text-2xl md:text-3xl mt-2 leading-tight">
            Every registration wins.
          </h3>
          <p className="text-forest/70 text-sm mt-2 leading-relaxed">
            Submit the form on this page and you unlock one guaranteed spin on
            the wheel &mdash; one of six curated rewards, including a chance at
            an <strong>iPhone 17 voucher</strong>.
          </p>

          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3 text-[12px]">
            {PRIZE_TYPES.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-forest">
                <span
                  className="inline-block w-3 h-3 rounded-sm flex-none"
                  style={{ background: t.color }}
                  aria-hidden="true"
                />
                <span>{t.label}</span>
              </li>
            ))}
          </ul>

          <p className="text-[11px] text-forest/50 leading-relaxed mt-4 pt-3 border-t border-forest/10">
            <strong className="text-forest/70">Terms:</strong> {WHEEL_TERMS}
          </p>
        </div>
      </div>
    </div>
  );
}
